/*

This function updates the bucket with fresh servers statuses.

We update only three bucket properties, which are objects used as hash maps.  Updating the bucket
state may be really costly in term of CPU usage.  Three ways were tested:

* The obvious way would be to use Vue.set to update each map value

* A more complicated way would be to iterate recursively over all values and use Vue.set /
  Vue.delete where needed to patch the store value.  See the `softSet` function below.

* Another way would be to create a whole new (non-reactive) bucket state object, update it, and then
  replace the old bucket state in one shot

There is two things to consider:

* Updating a whole object instead of its sub-values can produce useless Vue component updates.
  Example:
    Let's say we have a store like this `{ metrics: { latency: 1, cpu: 10 } }`
    * if we update the metric object like this `store.metrics = { latency: 1, cpu: 11 }`,
      components listening to `latency` changes will be updated
    * if we update only the cpu property `store.metrics.cpu = 11`, only components listening to
      `cpu` are updated
  This may not be a huge issue but we have to consider this.

* Updating anything in the state can be *very* costly in development mode when vuex is configured in
  strict mode. For some reason, each Vue.set implies notifying a whole bunch of notification, and
  using a reactive setter more than one time may freeze the page for a long time.

Knowing those two constraint, we chose the third way: updating the bucket state in non-reactive
objects, ther replacing the whole bucket.  This may be reconsidered in the future.

*/

function parseLegacyMetric(value) {
  // Ugly parsing for legacy metrics
  let number
  let unit
  let aggregable
  if (typeof value === 'number' || typeof value === 'boolean') {
    number = value
    unit = null
    aggregable = typeof value === 'number'
  } else if (typeof value === 'string') {
    const numericVals = value.match(/[+-]?\d+(\.\d+)?/g)
    // If there is no numeric value or more than one, this metric is unitless and non aggregable
    if (!numericVals || numericVals.length > 1) {
      number = value
      unit = null
      aggregable = false
    } else {
      number = numericVals[0]
      unit = value.replace(number, '') === '' ? null : value.replace(number, '').trim()
      aggregable = true
    }
  }
  return { unit, aggregable }
}

function updateOrPush(collection, item, fn) {
  const index = collection.findIndex(fn)
  if (index < 0) {
    collection.push(item)
  } else {
    collection[index] = item
  }
}

export default function updateBucketState(bucketState, newServerStatuses, frontendMetrics) {
  // Collect updated process keys
  const liveProcesses = new Set()
  // Collect updated server names
  const updatedServers = new Set()

  // Let's make stuff non-reactive!
  const metrics = Object.assign({}, bucketState.metrics)
  const processes = bucketState.processes.slice()
  const servers = bucketState.servers.slice()

  newServerStatuses.forEach(serverStatus => {
    updatedServers.add(serverStatus.server_name)

    if (window.DEBUG == true)
      console.log(serverStatus)
    // Update server infos
    const serverInfos = {
      realtime_update_at: Date.now(),
    }
    Object.keys(serverStatus)
      .filter(key => key !== 'data')
      .forEach(key => {
        serverInfos[key] = serverStatus[key]
      })
    Object.assign(serverInfos, serverStatus.data.server)
    serverInfos.active = serverStatus.data.server.active !== false

    updateOrPush(
      servers,
      Object.freeze(serverInfos),
      other => serverInfos.server_name === other.server_name,
    )

    serverStatus.data.process.forEach(process => {
      process.server = serverStatus.server_name

      // Update process infos
      updateOrPush(
        processes,
        Object.freeze(process),
        other => process.server === other.server && process.pid === other.pid,
      )
      liveProcesses.add(process)

      // For each process, update the list of available metrics and their unit and aggregable type
      for (const metricName in process.axm_monitor) {
        const metricObj = process.axm_monitor[metricName]
        // If the metric uses the new unit + type api, skip the ugly parsing
        let metric
        if (metricObj.unit) {
          metric = { unit: metricObj.unit, aggregable: true }
        } else if (!metrics[metricName]) {
          metric = parseLegacyMetric(metricObj.value)
        }
        if (metric) {
          if (metricObj.limits) {
            metric.limits = metricObj.limits
          } else if (metricObj.alert) {
            const growing = metricObj.alert.cmp === '>'
            metric.limits = [
              { level: 0, criticality: growing ? 0 : 1 },
              { level: metricObj.alert.value, criticality: growing ? 1 : 0 },
            ]
          }
          metrics[metricName] = Object.freeze(metric)
        }
      }
    })
  })

  if (frontendMetrics) {
    for (const metric of frontendMetrics) {
      if (!metrics.hasOwnProperty(metric.name)) {
        metrics[metric.name] = {
          unit: metric.unit,
          aggregable: true,
        }
      }
    }
  }

  return Object.assign({}, bucketState, {
    // Remove outdated processes
    processes: processes.filter(
      process => !updatedServers.has(process.server) || liveProcesses.has(process),
    ),
    metrics,
    servers,
  })
}

/*
// This is a quick and dirty function to update a reactive object recursively, instead of replacing
// it entirely.  See top comment for information.
import Vue from 'vue'

const DEBUG = false
function typeOf(value) {
  if (typeof value === 'object' && value) {
    if (Array.isArray(value)) return 'array'
    return 'object'
  }
  return 'primitive'
}

function softSetArray(state, key, value, stack) {
  const stateValue = state[key]
  value.forEach((item, index) => {
    softSet(stateValue, index, item, stack)
  })
  stateValue.splice(value.length)
}

function softSetNew(state, key, value) {
  Vue.set(state, key, value)
}

function softSetObject(state, key, value, stack) {
  const stateValue = state[key]
  for (const key in value) {
    softSet(stateValue, key, value[key], stack)
  }
  for (const key in stateValue) {
    if (!value.hasOwnProperty(key)) {
      Vue.delete(stateValue, key)
    }
  }
}
function softSetDirect(state, key, value) {
  state[key] = value
}

function softSet(state, key, value, stack) {
  if (DEBUG) {
    if (!stack) stack = [key]
    else stack = stack.concat(key)
  }

  if (state.hasOwnProperty(key)) {
    const stateValue = state[key]
    const valueType = typeOf(value)
    if (valueType !== typeOf(stateValue) || valueType === 'primitive') {
      if (state[key] !== value) {
        DEBUG && console.log(`${stack.join(' > ')} D ${state[key]} = ${value}`)
        softSetDirect(state, key, value)
      }
    } else if (valueType === 'object') {
      softSetObject(state, key, value, stack)
    } else { // array
      softSetArray(state, key, value)
    }
  } else {
    DEBUG && console.log(`${stack.join(' > ')} N`)
    softSetNew(state, key, value)
  }
}
*/
